import i18n from '@/i18n';

export type SupportedCurrency = 'EUR' | 'USD';

export interface CurrencyFormatOptions {
  currency?: SupportedCurrency;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export function getCurrentLocale(): string {
  // i18n.language may be like 'de', 'en', 'es', 'fr', 'it'; map to proper BCP47
  const lang = i18n.language || 'de';
  if (lang.startsWith('de')) return 'de-DE';
  if (lang.startsWith('en')) return 'en-US';
  return 'de-DE';
}

export function formatNumber(value: number, opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number }): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: opts?.minimumFractionDigits,
    maximumFractionDigits: opts?.maximumFractionDigits,
  }).format(value);
}

export function formatCurrency(amount: number, options: CurrencyFormatOptions = {}): string {
  const locale = getCurrentLocale();
  const currency = options.currency || 'EUR';
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);
}

export function formatCompactNumber(value: number, maximumFractionDigits = 1): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits,
  }).format(value);
}

export function parseCurrencyToNumber(input: string): number {
  // Best-effort parse considering current locale separators
  const locale = getCurrentLocale();
  const example = 1234.5;
  const parts = new Intl.NumberFormat(locale).formatToParts(example);
  const group = parts.find(p => p.type === 'group')?.value || ',';
  const decimal = parts.find(p => p.type === 'decimal')?.value || '.';

  // Remove currency symbols and spaces
  const sanitized = input
    .replace(/\s/g, '')
    .replace(/[€$]/g, '')
    .replace(new RegExp(`\\${group}`, 'g'), '')
    .replace(decimal, '.');

  const n = Number(sanitized);
  return Number.isFinite(n) ? n : 0;
}


